/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { PlatformData } from './PlatformData';
import {
    PlatformDataFromJSON,
    PlatformDataFromJSONTyped,
    PlatformDataToJSON,
} from './PlatformData';
import type { Network } from './Network';
import {
    NetworkFromJSON,
    NetworkFromJSONTyped,
    NetworkToJSON,
} from './Network';
import type { RegisterOnlyFlow } from './RegisterOnlyFlow';
import {
    RegisterOnlyFlowFromJSON,
    RegisterOnlyFlowFromJSONTyped,
    RegisterOnlyFlowToJSON,
} from './RegisterOnlyFlow';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';
import type { Feature } from './Feature';
import {
    FeatureFromJSON,
    FeatureFromJSONTyped,
    FeatureToJSON,
} from './Feature';
import type { Card } from './Card';
import {
    CardFromJSON,
    CardFromJSONTyped,
    CardToJSON,
} from './Card';

/**
 * 
 * @export
 * @interface Config
 */
export interface Config {
    /**
     * 
     * @type {string}
     * @memberof Config
     */
    hubspotId: string;
    /**
     * 
     * @type {{ [key: string]: Network; }}
     * @memberof Config
     */
    networks: { [key: string]: Network; };
    /**
     * 
     * @type {Array<Card>}
     * @memberof Config
     */
    faq: Array<Card>;
    /**
     * 
     * @type {string}
     * @memberof Config
     */
    investNowUrl: string;
    /**
     * 
     * @type {string}
     * @memberof Config
     */
    tanganyTosUrl: string;
    /**
     * 
     * @type {string}
     * @memberof Config
     */
    cashlinkWalletTosUrl: string;
    /**
     * 
     * @type {Array<string>}
     * @memberof Config
     */
    commonPasswordStrings: Array<string>;
    /**
     * 
     * @type {Document}
     * @memberof Config
     */
    identificationFormDoc: Document | null;
    /**
     * 
     * @type {Document}
     * @memberof Config
     */
    effectaAccountSetupDocument: Document;
    /**
     * 
     * @type {Document}
     * @memberof Config
     */
    cashlinkTosDocument: Document;
    /**
     * 
     * @type {Document}
     * @memberof Config
     */
    effectaTermsConditions: Document;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    allowVibCopy: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    identificationInPerson: boolean;
    /**
     * 
     * @type {Array<string>}
     * @memberof Config
     */
    marketingOptsEnabled: Array<string>;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    friendVouchersEnabled: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    ewpgSendEmailsToInvestorsEnabled: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    bankAccountRequired: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    securitiesDepositAccountRequired: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    taxInformationRequired: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Config
     */
    telephoneNumberRequired: boolean;
    /**
     * 
     * @type {Feature}
     * @memberof Config
     */
    features: Feature;
    /**
     * 
     * @type {RegisterOnlyFlow}
     * @memberof Config
     */
    registerOnlyFlow: RegisterOnlyFlow;
    /**
     * 
     * @type {string}
     * @memberof Config
     */
    googleTagManagerId: string;
    /**
     * 
     * @type {PlatformData}
     * @memberof Config
     */
    platformData: PlatformData;
}

/**
 * Check if a given object implements the Config interface.
 */
export function instanceOfConfig(value: object): value is Config {
    if (!('hubspotId' in value) || value['hubspotId'] === undefined) return false;
    if (!('networks' in value) || value['networks'] === undefined) return false;
    if (!('faq' in value) || value['faq'] === undefined) return false;
    if (!('investNowUrl' in value) || value['investNowUrl'] === undefined) return false;
    if (!('tanganyTosUrl' in value) || value['tanganyTosUrl'] === undefined) return false;
    if (!('cashlinkWalletTosUrl' in value) || value['cashlinkWalletTosUrl'] === undefined) return false;
    if (!('commonPasswordStrings' in value) || value['commonPasswordStrings'] === undefined) return false;
    if (!('identificationFormDoc' in value) || value['identificationFormDoc'] === undefined) return false;
    if (!('effectaAccountSetupDocument' in value) || value['effectaAccountSetupDocument'] === undefined) return false;
    if (!('cashlinkTosDocument' in value) || value['cashlinkTosDocument'] === undefined) return false;
    if (!('effectaTermsConditions' in value) || value['effectaTermsConditions'] === undefined) return false;
    if (!('allowVibCopy' in value) || value['allowVibCopy'] === undefined) return false;
    if (!('identificationInPerson' in value) || value['identificationInPerson'] === undefined) return false;
    if (!('marketingOptsEnabled' in value) || value['marketingOptsEnabled'] === undefined) return false;
    if (!('friendVouchersEnabled' in value) || value['friendVouchersEnabled'] === undefined) return false;
    if (!('ewpgSendEmailsToInvestorsEnabled' in value) || value['ewpgSendEmailsToInvestorsEnabled'] === undefined) return false;
    if (!('bankAccountRequired' in value) || value['bankAccountRequired'] === undefined) return false;
    if (!('securitiesDepositAccountRequired' in value) || value['securitiesDepositAccountRequired'] === undefined) return false;
    if (!('taxInformationRequired' in value) || value['taxInformationRequired'] === undefined) return false;
    if (!('telephoneNumberRequired' in value) || value['telephoneNumberRequired'] === undefined) return false;
    if (!('features' in value) || value['features'] === undefined) return false;
    if (!('registerOnlyFlow' in value) || value['registerOnlyFlow'] === undefined) return false;
    if (!('googleTagManagerId' in value) || value['googleTagManagerId'] === undefined) return false;
    if (!('platformData' in value) || value['platformData'] === undefined) return false;
    return true;
}

export function ConfigFromJSON(json: any): Config {
    return ConfigFromJSONTyped(json, false);
}

export function ConfigFromJSONTyped(json: any, ignoreDiscriminator: boolean): Config {
    if (json == null) {
        return json;
    }
    return {
        
        'hubspotId': json['hubspot_id'],
        'networks': (mapValues(json['networks'], NetworkFromJSON)),
        'faq': ((json['faq'] as Array<any>).map(CardFromJSON)),
        'investNowUrl': json['invest_now_url'],
        'tanganyTosUrl': json['tangany_tos_url'],
        'cashlinkWalletTosUrl': json['cashlink_wallet_tos_url'],
        'commonPasswordStrings': json['common_password_strings'],
        'identificationFormDoc': DocumentFromJSON(json['identification_form_doc']),
        'effectaAccountSetupDocument': DocumentFromJSON(json['effecta_account_setup_document']),
        'cashlinkTosDocument': DocumentFromJSON(json['cashlink_tos_document']),
        'effectaTermsConditions': DocumentFromJSON(json['effecta_terms_conditions']),
        'allowVibCopy': json['allow_vib_copy'],
        'identificationInPerson': json['identification_in_person'],
        'marketingOptsEnabled': json['marketing_opts_enabled'],
        'friendVouchersEnabled': json['friend_vouchers_enabled'],
        'ewpgSendEmailsToInvestorsEnabled': json['ewpg_send_emails_to_investors_enabled'],
        'bankAccountRequired': json['bank_account_required'],
        'securitiesDepositAccountRequired': json['securities_deposit_account_required'],
        'taxInformationRequired': json['tax_information_required'],
        'telephoneNumberRequired': json['telephone_number_required'],
        'features': FeatureFromJSON(json['features']),
        'registerOnlyFlow': RegisterOnlyFlowFromJSON(json['register_only_flow']),
        'googleTagManagerId': json['google_tag_manager_id'],
        'platformData': PlatformDataFromJSON(json['platform_data']),
    };
}

export function ConfigToJSON(value?: Config | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'hubspot_id': value['hubspotId'],
        'networks': (mapValues(value['networks'], NetworkToJSON)),
        'faq': ((value['faq'] as Array<any>).map(CardToJSON)),
        'invest_now_url': value['investNowUrl'],
        'tangany_tos_url': value['tanganyTosUrl'],
        'cashlink_wallet_tos_url': value['cashlinkWalletTosUrl'],
        'common_password_strings': value['commonPasswordStrings'],
        'identification_form_doc': DocumentToJSON(value['identificationFormDoc']),
        'effecta_account_setup_document': DocumentToJSON(value['effectaAccountSetupDocument']),
        'cashlink_tos_document': DocumentToJSON(value['cashlinkTosDocument']),
        'effecta_terms_conditions': DocumentToJSON(value['effectaTermsConditions']),
        'allow_vib_copy': value['allowVibCopy'],
        'identification_in_person': value['identificationInPerson'],
        'marketing_opts_enabled': value['marketingOptsEnabled'],
        'friend_vouchers_enabled': value['friendVouchersEnabled'],
        'ewpg_send_emails_to_investors_enabled': value['ewpgSendEmailsToInvestorsEnabled'],
        'bank_account_required': value['bankAccountRequired'],
        'securities_deposit_account_required': value['securitiesDepositAccountRequired'],
        'tax_information_required': value['taxInformationRequired'],
        'telephone_number_required': value['telephoneNumberRequired'],
        'features': FeatureToJSON(value['features']),
        'register_only_flow': RegisterOnlyFlowToJSON(value['registerOnlyFlow']),
        'google_tag_manager_id': value['googleTagManagerId'],
        'platform_data': PlatformDataToJSON(value['platformData']),
    };
}

